import { faList, faPaperclip } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { createFileRoute, Outlet, redirect } from '@tanstack/react-router';
import { OrderHubDetailError } from '@/app/(client)/orders-hub/-components/OrderHubDetailError';
import { OrderHubDetailHeader } from '@/app/(client)/orders-hub/-components/OrderHubDetailHeader';
import { OrderHubTracingLinkAction } from '@/app/(client)/orders-hub/-components/OrderHubTracingLinkAction';
import { SendToCmmDialog } from '@/app/(client)/orders-hub/-components/SendToCmmDialog';
import { SendToTracingDialog } from '@/app/(client)/orders-hub/-components/SendToTracingDialog';
import { WorkflowStatusCell } from '@/app/(client)/orders-hub/-components/WorkflowStatusCell';
import { useCmmSendFlow } from '@/app/(client)/orders-hub/-hooks/useCmmSendFlow';
import { counterpartyProfileQueryOptions } from '@/app/(client)/orders-hub/-hooks/useOrderHubCounterpartyProfile';
import { useOrderHubHeaderParties } from '@/app/(client)/orders-hub/-hooks/useOrderHubHeaderParties';
import { useOrderHubOrderActions } from '@/app/(client)/orders-hub/-hooks/useOrderHubOrderActions';
import {
  orderHubDetailQueryOptions,
  useOrderHubOrderDetail,
} from '@/app/(client)/orders-hub/-hooks/useOrderHubOrderDetail';
import { orderHubOrderLinesQueryOptions } from '@/app/(client)/orders-hub/-hooks/useOrderHubOrderLinesList';
import { useOrderHubReadiness } from '@/app/(client)/orders-hub/-hooks/useOrderHubReadiness';
import { useOrderHubTracingUnlink } from '@/app/(client)/orders-hub/-hooks/useOrderHubTracingUnlink';
import { useOrdersReadiness } from '@/app/(client)/orders-hub/-hooks/useOrdersReadiness';
import { useSendToTracingFlow } from '@/app/(client)/orders-hub/-hooks/useSendToTracingFlow';
import { orderCellSummary } from '@/app/(client)/orders-hub/-utils/sendCellState';
import PageTabs from '@/components/shared/PageTabs';
import { attachmentTemplatesQueryOptions } from '@/hooks/useAttachmentTemplatesForImport';
import { useBreadcrumbLabel } from '@/hooks/useBreadcrumbLabel';
import { useFeatures } from '@/hooks/useFeatures';
import { ensurePermissions } from '@/hooks/usePermissions';
import { useTranslator } from '@/hooks/useTranslator';
import { PLATFORM_FEATURES } from '@/lib/constants/features';
import { queryClient } from '@/lib/utils/queryClient';
import { useSessionStore } from '@/store/sessionStore';

export const Route = createFileRoute(
  '/(client)/orders-hub/(pages)/(detail)/$id',
)({
  beforeLoad: async ({ params, location }) => {
    const { orders } = await ensurePermissions();
    if (!orders.canRead) throw redirect({ to: '/dashboard' });

    const bare = `/orders-hub/${params.id}`;
    if (location.pathname === bare || location.pathname === `${bare}/`) {
      throw redirect({
        to: '/orders-hub/$id/overview',
        params: { id: params.id },
      });
    }
  },
  loader: async ({ params }) => {
    // Warm every readiness input so the header badges paint their final value
    // without a flash on navigation.
    // Attachment templates belong to Cascade Tracing, a separate feature from
    // Order Hub — requesting them without it 403s on every order page.
    const cascadeTracing = useSessionStore
      .getState()
      .user?.company?.enabledFeatures?.some(
        f => f.id === PLATFORM_FEATURES.CASCADE_TRACING,
      );

    const [order] = await Promise.all([
      queryClient.ensureQueryData(orderHubDetailQueryOptions(params.id)),
      queryClient.ensureQueryData(orderHubOrderLinesQueryOptions(params.id)),
      cascadeTracing
        ? queryClient
            .ensureQueryData(attachmentTemplatesQueryOptions)
            .catch(() => ({ data: [] }))
        : Promise.resolve(),
    ]);
    if (order.supplierCompanyId) {
      await queryClient.ensureQueryData(
        counterpartyProfileQueryOptions(order.supplierCompanyId),
      );
    }
  },
  errorComponent: OrderHubDetailError,
  component: OrderHubDetailLayout,
});

function OrderHubDetailLayout() {
  const { id } = Route.useParams();
  const { isOrdersHubEnabled } = useFeatures();
  const { getTranslation: t } = useTranslator({ page: 'ordersHub' });
  const { getTranslation: tShared } = useTranslator({ page: 'shared' });
  const { data: order } = useOrderHubOrderDetail(id);
  const readiness = useOrderHubReadiness(id);
  const parties = useOrderHubHeaderParties(order);
  const {
    toggleArchive,
    remove,
    exportOrder,
    isArchiving,
    isDeleting,
    isExporting,
  } = useOrderHubOrderActions(id, order?.isArchived ?? false);
  const sendFlow = useSendToTracingFlow();
  const cmmFlow = useCmmSendFlow();
  // With lines: the overview's line cells share this cache entry.
  const { data: sendReadiness } = useOrdersReadiness([id], {
    includeLines: true,
  });
  const orderSendReadiness = sendReadiness?.[id];
  const tracingUnlink = useOrderHubTracingUnlink(id);
  // Show the order number in the AppHeader breadcrumb instead of the raw id.
  useBreadcrumbLabel(id, order?.orderNumber);

  if (!isOrdersHubEnabled) return null;

  const canSendToTracing = order?.permissions?.includes('edit') ?? false;
  const supplierNoActiveUser =
    readiness?.tracing.checks.some(
      check =>
        check.key === 'supplier_active_user' && check.status === 'missing',
    ) ?? false;

  const tracingCell = order && (
    <WorkflowStatusCell
      workflow='tracing'
      summary={
        sendFlow.isSessionReady
          ? orderCellSummary(
              sendFlow.ctx.isCascadeTracingEnabled,
              orderSendReadiness?.tracing,
            )
          : null
      }
      withWorkflowLabel
      isPending={sendFlow.isSending(order.id)}
      supplierNoActiveUser={supplierNoActiveUser}
      {...(canSendToTracing
        ? {
            onSend: () =>
              sendFlow.start(
                [
                  {
                    kind: 'order',
                    order,
                    readiness: orderSendReadiness?.tracing,
                  },
                ],
                'orders',
              ),
          }
        : {})}
      t={t}
    />
  );

  const cmmCell = order && (
    <WorkflowStatusCell
      workflow='cmm'
      summary={
        sendFlow.isSessionReady && cmmFlow.isLoaded
          ? orderCellSummary(
              cmmFlow.ctx.isCmmIntegrationEnabled,
              orderSendReadiness?.cmm,
            )
          : null
      }
      withWorkflowLabel
      isPending={cmmFlow.isSending(order.id)}
      {...(cmmFlow.canSend(order.permissions)
        ? {
            onSend: () =>
              cmmFlow.start(
                [{ kind: 'order', order, readiness: orderSendReadiness?.cmm }],
                [{ orderId: order.id }],
                'orders',
              ),
          }
        : { noPermission: true })}
      t={t}
    />
  );

  const tracingLink = order?.tracingLink;
  const tracingLinkAction = order && tracingLink && (
    <OrderHubTracingLinkAction
      link={tracingLink}
      orderType={order.orderType}
      canEdit={canSendToTracing}
      open={tracingUnlink.open}
      onOpenChange={tracingUnlink.setOpen}
      isUnlinking={tracingUnlink.isUnlinking}
      onConfirm={() => void tracingUnlink.handleConfirm(tracingLink)}
      t={t}
    />
  );

  return (
    <div className='flex flex-col flex-1 min-h-0 overflow-y-auto'>
      {order && (
        <OrderHubDetailHeader
          order={order}
          readiness={readiness}
          supplier={parties.supplier}
          receiver={parties.receiver}
          onToggleArchive={toggleArchive}
          onDelete={remove}
          onExport={exportOrder}
          isArchiving={isArchiving}
          isDeleting={isDeleting}
          isExporting={isExporting}
          canEdit={order.permissions?.includes('edit') ?? false}
          canDelete={order.permissions?.includes('delete') ?? false}
          {...(tracingCell
            ? { tracingAction: tracingCell, tracingStatusChip: tracingCell }
            : {})}
          isCmmIntegrationEnabled={cmmFlow.ctx.isCmmIntegrationEnabled}
          {...(cmmCell ? { cmmAction: cmmCell } : {})}
          {...(tracingLinkAction ? { tracingLinkAction } : {})}
        />
      )}

      <SendToCmmDialog
        open={cmmFlow.open}
        onOpenChange={cmmFlow.setOpen}
        preflight={cmmFlow.preflight}
        scope={cmmFlow.scope}
        isPending={cmmFlow.isPending}
        onConfirm={cmmFlow.confirm}
        onSeeLines={cmmFlow.goToLines}
        t={t}
      />

      <SendToTracingDialog
        open={sendFlow.open}
        onOpenChange={sendFlow.setOpen}
        preflight={sendFlow.preflight}
        scope={sendFlow.scope}
        templateName={sendFlow.templateName}
        isPending={sendFlow.isPending}
        onConfirm={sendFlow.confirm}
        onFixTemplate={sendFlow.goToTemplateSetting}
        suppliers={sendFlow.suppliers}
        suppliersResolved={sendFlow.suppliersResolved}
        t={t}
      />

      <div className='sticky top-0 z-20 bg-background px-4 sm:px-6'>
        <PageTabs
          mode='path'
          moreLabel={tShared('moreTabs')}
          tabs={[
            {
              key: 'overview',
              label: t('detail.tabs.overview'),
              icon: <FontAwesomeIcon icon={faList} className='size-4' />,
              to: `/orders-hub/${id}/overview`,
            },
            {
              key: 'attachments',
              label: t('detail.tabs.attachments'),
              icon: <FontAwesomeIcon icon={faPaperclip} className='size-4' />,
              to: `/orders-hub/${id}/attachments`,
            },
          ]}
        />
      </div>

      <div className='@container flex flex-col flex-1 min-h-[70dvh] min-w-0'>
        <Outlet />
      </div>
    </div>
  );
}
